import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Add, Close } from "@mui/icons-material";
import useAccounts from "../hooks/useAccounts";
import AccountsDashboard from "../components/AccountsDashboard";
import AccountList from "../components/AccountList";
import AccountForm from "../components/AccountForm";
import InvoiceList from "../components/InvoiceList";
import InvoiceForm from "../components/InvoiceForm";
import BillList from "../components/BillList";
import BillForm from "../components/BillForm";
import JournalList from "../components/JournalList";
import JournalForm from "../components/JournalForm";
import ReceiptForm from "../components/ReceiptForm";
import PaymentForm from "../components/PaymentForm";
import BankAccountList from "../components/BankAccountList";
import LedgerViewer from "../components/LedgerViewer";
import Modal from "../../../components/common/Modal";

const AccountsPage = () => {
  const {
    accounts,
    invoices,
    bills,
    journals,
    journalLines,
    bankAccounts,
    loading,
    error,
    setError,
    loadJournalLines,
    createAccount,
    updateAccount,
    deleteAccount,
    createInvoice,
    updateInvoice,
    createBill,
    updateBill,
    createJournal,
    updateJournal,
    createReceipt,
    createPayment,
  } = useAccounts();

  const [tab, setTab] = useState(0);
  const [modalType, setModalType] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const modalTitle = useMemo(() => {
    const titles = {
      account: editItem ? "Edit Account" : "New Account",
      invoice: editItem ? "Edit Invoice" : "New Invoice",
      bill: editItem ? "Edit Bill" : "New Bill",
      journal: editItem ? "Edit Journal" : "New Journal",
      receipt: "Record Receipt",
      payment: "Record Payment",
      ledger: "Journal Ledger Lines",
    };
    return titles[modalType] || "";
  }, [modalType, editItem]);

  const closeModal = () => {
    setModalType(null);
    setEditItem(null);
  };

  const renderHeaderAction = () => {
    if (tab === 0)
      return (
        <Button
          startIcon={<Add />}
          variant="contained"
          onClick={() => setModalType("account")}
        >
          New Account
        </Button>
      );
    if (tab === 1)
      return (
        <Button
          startIcon={<Add />}
          variant="contained"
          onClick={() => setModalType("invoice")}
        >
          New Invoice
        </Button>
      );
    if (tab === 2)
      return (
        <Button
          startIcon={<Add />}
          variant="contained"
          onClick={() => setModalType("bill")}
        >
          New Bill
        </Button>
      );
    if (tab === 3)
      return (
        <Button
          startIcon={<Add />}
          variant="contained"
          onClick={() => setModalType("journal")}
        >
          New Journal
        </Button>
      );
    if (tab === 4)
      return (
        <Button
          startIcon={<Add />}
          variant="contained"
          onClick={() => setModalType("receipt")}
        >
          Record Receipt
        </Button>
      );
    if (tab === 5)
      return (
        <Button
          startIcon={<Add />}
          variant="contained"
          onClick={() => setModalType("payment")}
        >
          Record Payment
        </Button>
      );
    return null;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Accounts Module
          </Typography>
          <Typography color="text.secondary">
            Chart of accounts, invoices, bills, journals, banking, and ledger.
          </Typography>
        </Box>
        {renderHeaderAction()}
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={() => setError(null)}>
              <Close fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <AccountsDashboard
          accounts={accounts}
          invoices={invoices}
          bills={bills}
          journals={journals}
        />
      </Box>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ mb: 3 }}
        variant="scrollable"
      >
        <Tab label="Accounts" />
        <Tab label="Invoices" />
        <Tab label="Bills" />
        <Tab label="Journals" />
        <Tab label="Receipts" />
        <Tab label="Payments" />
        <Tab label="Bank Accounts" />
      </Tabs>

      {tab === 0 && (
        <AccountList
          accounts={accounts}
          onEdit={(item) => {
            setEditItem(item);
            setModalType("account");
          }}
          onDelete={deleteAccount}
        />
      )}

      {tab === 1 && (
        <InvoiceList
          invoices={invoices}
          onEdit={(item) => {
            setEditItem(item);
            setModalType("invoice");
          }}
        />
      )}

      {tab === 2 && (
        <BillList
          bills={bills}
          onEdit={(item) => {
            setEditItem(item);
            setModalType("bill");
          }}
        />
      )}

      {tab === 3 && (
        <JournalList
          journals={journals}
          onView={async (journal) => {
            await loadJournalLines(journal.id);
            setEditItem(journal);
            setModalType("ledger");
          }}
        />
      )}

      {tab === 4 && (
        <Typography color="text.secondary">
          Use "Record Receipt" to capture customer receipt entries.
        </Typography>
      )}

      {tab === 5 && (
        <Typography color="text.secondary">
          Use "Record Payment" to capture vendor payment entries.
        </Typography>
      )}

      {tab === 6 && <BankAccountList bankAccounts={bankAccounts} />}

      <Modal isOpen={!!modalType} onClose={closeModal} title={modalTitle}>
        {modalType === "account" && (
          <AccountForm
            initialData={editItem}
            onSubmit={async (payload) => {
              if (editItem) await updateAccount(editItem.id, payload);
              else await createAccount(payload);
              closeModal();
            }}
            onCancel={closeModal}
            loading={loading}
          />
        )}

        {modalType === "invoice" && (
          <InvoiceForm
            initialData={editItem}
            onSubmit={async (payload) => {
              if (editItem) await updateInvoice(editItem.id, payload);
              else await createInvoice(payload);
              closeModal();
            }}
            onCancel={closeModal}
            loading={loading}
          />
        )}

        {modalType === "bill" && (
          <BillForm
            initialData={editItem}
            onSubmit={async (payload) => {
              if (editItem) await updateBill(editItem.id, payload);
              else await createBill(payload);
              closeModal();
            }}
            onCancel={closeModal}
            loading={loading}
          />
        )}

        {modalType === "journal" && (
          <JournalForm
            initialData={editItem}
            onSubmit={async (payload) => {
              if (editItem) await updateJournal(editItem.id, payload);
              else await createJournal(payload);
              closeModal();
            }}
            onCancel={closeModal}
            loading={loading}
          />
        )}

        {modalType === "receipt" && (
          <ReceiptForm
            onSubmit={async (payload) => {
              await createReceipt(payload);
              closeModal();
            }}
            onCancel={closeModal}
            loading={loading}
          />
        )}

        {modalType === "payment" && (
          <PaymentForm
            onSubmit={async (payload) => {
              await createPayment(payload);
              closeModal();
            }}
            onCancel={closeModal}
            loading={loading}
          />
        )}

        {modalType === "ledger" && <LedgerViewer lines={journalLines} />}
      </Modal>
    </Container>
  );
};

export default AccountsPage;
